import React, { useState, useEffect } from 'react';
import axiosInstance from '../apis/axiosConfig';
import { toast } from 'react-toastify';
import { FaTrash, FaCheck, FaTimes, FaCommentDots, FaStar, FaSearch, FaPlus, FaEye, FaQuoteLeft } from 'react-icons/fa';
import { getFullImageUrl } from '../utils/url';
import { compressImageFile } from '../utils/imageCompressor';
import '../pages_premium.css';
import './TestimonialsList.css';
import AdminPagination from '../components/common/AdminPagination';

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

function TestimonialsList() {
    const [testimonials, setTestimonials] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [selectedTestimonial, setSelectedTestimonial] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);
    
    // Add Testimonial Modal State
    const [showAddModal, setShowAddModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [formData, setFormData] = useState({
        reviewer_name: '',
        role: 'Student',
        university: '',
        rating: 5,
        review_text: '',
        avatar: '',
        target_type: 'general',
        status: 'approved'
    });

    const fetchTestimonials = async () => {
        try {
            const res = await axiosInstance.get('/testimonials');
            setTestimonials(res.data || []);
        } catch (error) {
            console.error('Error fetching testimonials:', error);
            toast.error('Failed to fetch testimonials');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTestimonials();
    }, []);

    const updateStatus = async (id, newStatus) => {
        try {
            await axiosInstance.put(`/testimonials/${id}`, { status: newStatus });
            toast.success(`Testimonial ${newStatus}`);
            fetchTestimonials();
        } catch (error) {
            console.error('Error updating testimonial status:', error);
            toast.error('Failed to update testimonial');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this testimonial?")) {
            try {
                await axiosInstance.delete(`/testimonials/${id}`);
                toast.success('Testimonial deleted successfully');
                if (selectedTestimonial && selectedTestimonial.id === id) {
                    setSelectedTestimonial(null);
                }
                fetchTestimonials();
            } catch (error) {
                console.error('Error deleting testimonial:', error);
                toast.error('Failed to delete testimonial');
            }
        }
    };

    const handleAvatarFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > MAX_FILE_SIZE_BYTES) {
            toast.error(`"${file.name}" exceeds the ${MAX_FILE_SIZE_MB} MB limit.`);
            e.target.value = '';
            return;
        }

        try {
            setUploadingImage(true);
            const optimized = await compressImageFile(file);
            const data = new FormData();
            data.append('image', optimized.file);

            const res = await axiosInstance.post('/upload', data, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (res.data && res.data.url) {
                setFormData(prev => ({ ...prev, avatar: res.data.url }));
                toast.success('Avatar image uploaded successfully!');
            }
        } catch (err) {
            console.error('Failed to upload image:', err);
            toast.error(err.response?.data?.message || 'Failed to upload image');
        } finally {
            setUploadingImage(false);
        }
        e.target.value = '';
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        if (!formData.reviewer_name.trim() || !formData.review_text.trim()) {
            toast.error('Name and Review Text are required!');
            return;
        }

        try {
            setSubmitting(true);
            await axiosInstance.post('/testimonials', formData);
            toast.success('Testimonial added successfully!');
            setShowAddModal(false);
            setFormData({
                reviewer_name: '',
                role: 'Student',
                university: '',
                rating: 5,
                review_text: '',
                avatar: '',
                target_type: 'general',
                status: 'approved'
            });
            fetchTestimonials();
        } catch (error) {
            console.error('Error adding testimonial:', error);
            toast.error('Failed to add testimonial');
        } finally {
            setSubmitting(false);
        }
    };

    const truncateReview = (text, maxLength = 45) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    const filtered = testimonials.filter(t => 
        (t.reviewer_name || t.Reviewer?.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (t.target_type || '').toLowerCase().includes(search.toLowerCase()) ||
        (t.review_text || '').toLowerCase().includes(search.toLowerCase())
    );

    const paginatedTestimonials = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    return (
        <div className="testimonials-list-page page-premium-wrapper">
            <div className="page-premium-content">
                
                {/* Hero Header */}
                <div className="page-hero-header">
                    <div className="ph-left">
                        <div className="ph-icon">
                            <FaCommentDots />
                        </div>
                        <div>
                            <h1 className="ph-title">Manage Testimonials</h1>
                            <p className="ph-subtitle">Review, approve, add, or delete student and provider feedback</p>
                        </div>
                    </div>
                    
                    <div className="ph-right d-flex align-items-center gap-3">
                        <button 
                            className="btn btn-primary font-weight-bold shadow-sm d-flex align-items-center gap-2 px-3.5 py-2.5 mr-3"
                            style={{ borderRadius: '12px' }}
                            onClick={() => setShowAddModal(true)}
                        >
                            <FaPlus /> Add Testimonial
                        </button>
                        <div className="ph-count-badge">
                            <span className="count-num">{testimonials.length}</span>
                            <span className="count-lbl">Total Reviews</span>
                        </div>
                    </div>
                </div>
                
                {/* Table Card */}
                <div className="premium-table-card">
                    <div className="ptable-toolbar">
                        <div className="ptable-title">
                            <span className="ptable-title-dot"></span>
                            Testimonials &amp; Reviews
                        </div>
                        <div className="ptable-search">
                            <FaSearch className="ptable-search-icon" />
                            <input
                                type="text"
                                placeholder="Search by user, type, review..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>
                    
                    <div className="ptable-wrap" style={{ overflowX: 'auto' }}>
                        <table className="table ptable align-middle mb-0" style={{ minWidth: '1100px', borderCollapse: 'separate', borderSpacing: 0, border: '1px solid #e2e8f0' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #cbd5e1' }}>
                                    <th style={{ width: '65px', padding: '14px 16px', borderRight: '1px solid #e2e8f0' }}>ID</th>
                                    <th style={{ minWidth: '180px', padding: '14px 16px', borderRight: '1px solid #e2e8f0' }}>Reviewer</th>
                                    <th style={{ minWidth: '180px', padding: '14px 16px', borderRight: '1px solid #e2e8f0' }}>Role / Location</th>
                                    <th style={{ minWidth: '120px', padding: '14px 16px', borderRight: '1px solid #e2e8f0' }}>Rating</th>
                                    <th style={{ minWidth: '240px', padding: '14px 16px', borderRight: '1px solid #e2e8f0' }}>Review Text</th>
                                    <th style={{ minWidth: '130px', padding: '14px 16px', borderRight: '1px solid #e2e8f0' }}>Status</th>
                                    <th className="text-center" style={{ minWidth: '130px', padding: '14px 16px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="7" className="text-center py-5 text-muted">
                                            Loading testimonials...
                                        </td>
                                    </tr>
                                ) : filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="text-center py-5 text-muted">
                                            No testimonials found. Click &quot;+ Add Testimonial&quot; above to create one.
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedTestimonials.map((review) => (
                                        <tr key={review.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                            <td style={{ color: '#64748b', fontWeight: 600, padding: '14px 16px', borderRight: '1px solid #e2e8f0' }}>#{review.id}</td>
                                            <td style={{ fontWeight: 600, color: '#0f172a', padding: '14px 16px', borderRight: '1px solid #e2e8f0' }}>
                                                <div className="d-flex align-items-center">
                                                    {review.avatar ? (
                                                        <img 
                                                            src={getFullImageUrl(review.avatar)} 
                                                            alt="Avatar" 
                                                            className="mr-2 rounded-circle" 
                                                            style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                                                        />
                                                    ) : null}
                                                    <span>{review.reviewer_name || (review.Reviewer ? review.Reviewer.name : `User #${review.user_id}`)}</span>
                                                </div>
                                            </td>
                                            <td style={{ color: '#475569', fontSize: '13px', padding: '14px 16px', borderRight: '1px solid #e2e8f0' }}>
                                                <div className="font-weight-bold">{review.role || 'Student'}</div>
                                                {review.university && (
                                                    <div className="text-muted small">{review.university}</div>
                                                )}
                                            </td>
                                            <td style={{ padding: '14px 16px', borderRight: '1px solid #e2e8f0' }}>
                                                <span className="rating-badge">
                                                    <FaStar style={{ color: '#eab308', fontSize: '11px' }} /> {review.rating}/5
                                                </span>
                                            </td>
                                            <td style={{ padding: '14px 16px', borderRight: '1px solid #e2e8f0' }}>
                                                <div style={{ color: '#334155', lineHeight: '1.4', fontSize: '13px', fontWeight: 500 }}>
                                                    {truncateReview(review.review_text, 45)}
                                                </div>
                                            </td>
                                            <td style={{ padding: '14px 16px', borderRight: '1px solid #e2e8f0' }}>
                                                <span className={`ptag ${review.status === 'approved' ? 'ptag-approved' : 'ptag-pending'}`}>
                                                    {review.status === 'approved' ? <FaCheck style={{ marginRight: 4 }} /> : <FaTimes style={{ marginRight: 4 }} />}
                                                    {review.status}
                                                </span>
                                            </td>
                                            <td className="text-center" style={{ padding: '14px 16px' }}>
                                                <div className="action-btn-group d-flex align-items-center justify-content-center gap-1">
                                                    <button 
                                                        className="icon-action-btn"
                                                        style={{ backgroundColor: '#0284c7', color: '#ffffff', borderColor: '#0284c7' }}
                                                        onClick={() => setSelectedTestimonial(review)}
                                                        title="View Full Testimonial"
                                                    >
                                                        <FaEye />
                                                    </button>
                                                    {review.status === 'pending' ? (
                                                        <button 
                                                            className="icon-action-btn icon-action-btn-approve" 
                                                            onClick={() => updateStatus(review.id, 'approved')}
                                                            title="Approve Testimonial"
                                                        >
                                                            <FaCheck />
                                                        </button>
                                                    ) : (
                                                        <button 
                                                            className="icon-action-btn icon-action-btn-unapprove" 
                                                            onClick={() => updateStatus(review.id, 'pending')}
                                                            title="Unapprove Testimonial"
                                                        >
                                                            <FaTimes />
                                                        </button>
                                                    )}
                                                    <button 
                                                        className="icon-action-btn icon-action-btn-delete" 
                                                        onClick={() => handleDelete(review.id)}
                                                        title="Delete Testimonial"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <AdminPagination
                        currentPage={currentPage}
                        totalItems={filtered.length}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={setPageSize}
                        itemLabel="testimonials"
                    />
                </div>
            </div>

            {/* ── VIEW TESTIMONIAL DETAILS MODAL POPUP ─────────────────────── */}
            {selectedTestimonial && (
                <div 
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Escape') setSelectedTestimonial(null); }}
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(6px)',
                        zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem'
                    }}
                    onClick={(e) => { if (e.target === e.currentTarget) setSelectedTestimonial(null); }}
                >
                    <div 
                        role="dialog"
                        aria-modal="true"
                        style={{
                            backgroundColor: '#ffffff', borderRadius: '24px', maxWidth: '580px', width: '100%',
                            maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', border: '1px solid #f1f5f9'
                        }}
                    >
                        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', borderTopLeftRadius: '24px', borderTopRightRadius: '24px' }}>
                            <div className="d-flex align-items-center">
                                <FaQuoteLeft className="text-primary mr-2" size={20} />
                                <h5 className="mb-0 font-weight-bold text-dark" style={{ fontSize: '18px' }}>
                                    Testimonial Details #{selectedTestimonial.id}
                                </h5>
                            </div>
                            <button onClick={() => setSelectedTestimonial(null)} style={{ border: 'none', background: '#e2e8f0', width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer' }}>
                                <FaTimes />
                            </button>
                        </div>

                        <div style={{ padding: '1.5rem' }}>
                            <div className="d-flex align-items-center gap-3 mb-4 p-3 rounded-2" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                {selectedTestimonial.avatar ? (
                                    <img 
                                        src={getFullImageUrl(selectedTestimonial.avatar)} 
                                        alt="Avatar" 
                                        className="mr-3 rounded-circle"
                                        style={{ width: '52px', height: '52px', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <div 
                                        className="mr-3 text-white font-weight-bold d-flex align-items-center justify-content-center rounded-circle"
                                        style={{ width: '48px', height: '48px', backgroundColor: '#0284c7', fontSize: '18px' }}
                                    >
                                        {(selectedTestimonial.reviewer_name || selectedTestimonial.Reviewer?.name || 'U').charAt(0).toUpperCase()}
                                    </div>
                                )}
                                <div>
                                    <h6 className="mb-1 font-weight-bold text-dark" style={{ fontSize: '16px' }}>
                                        {selectedTestimonial.reviewer_name || (selectedTestimonial.Reviewer ? selectedTestimonial.Reviewer.name : `User #${selectedTestimonial.user_id}`)}
                                    </h6>
                                    <div className="text-muted small font-weight-semibold">
                                        {selectedTestimonial.role || 'Student'} {selectedTestimonial.university ? ` · ${selectedTestimonial.university}` : ''}
                                    </div>
                                    <div className="mt-1">
                                        <span className="rating-badge">
                                            <FaStar style={{ color: '#eab308', fontSize: '12px' }} /> {selectedTestimonial.rating}/5 Stars
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <h6 className="font-weight-bold text-dark mb-2">Review Quote Text:</h6>
                            <div 
                                style={{
                                    backgroundColor: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '16px',
                                    padding: '1.25rem', color: '#1e293b', fontSize: '14px', lineHeight: '1.6',
                                    whiteSpace: 'pre-wrap', maxHeight: '250px', overflowY: 'auto'
                                }}
                            >
                                {`"${selectedTestimonial.review_text}"`}
                            </div>
                        </div>

                        <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #f1f5f9', backgroundColor: '#f8fafc', display: 'flex', justifyContent: 'space-between', borderBottomLeftRadius: '24px', borderBottomRightRadius: '24px' }}>
                            <button
                                className="btn btn-outline-danger font-weight-bold btn-sm px-3"
                                onClick={() => handleDelete(selectedTestimonial.id)}
                            >
                                <FaTrash className="mr-1" /> Delete
                            </button>
                            <button
                                className="btn btn-secondary font-weight-bold btn-sm px-4"
                                onClick={() => setSelectedTestimonial(null)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── ADD TESTIMONIAL MODAL POPUP ─────────────────────── */}
            {showAddModal && (
                <div 
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Escape') setShowAddModal(false); }}
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(6px)',
                        zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem'
                    }}
                    onClick={(e) => { if (e.target === e.currentTarget) setShowAddModal(false); }}
                >
                    <div 
                        role="dialog"
                        aria-modal="true"
                        style={{
                            backgroundColor: '#ffffff', borderRadius: '24px', maxWidth: '580px', width: '100%',
                            maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', border: '1px solid #f1f5f9'
                        }}
                    >
                        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <h5 className="mb-0 font-weight-bold text-dark" style={{ fontSize: '18px' }}>
                                Add New Testimonial
                            </h5>
                            <button onClick={() => setShowAddModal(false)} style={{ border: 'none', background: '#f1f5f9', width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer' }}>
                                <FaTimes />
                            </button>
                        </div>

                        <form onSubmit={handleAddSubmit} style={{ padding: '1.5rem' }}>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label htmlFor="reviewer_name" className="form-label font-weight-bold small text-muted">Reviewer Name *</label>
                                    <input 
                                        id="reviewer_name"
                                        type="text" className="form-control" placeholder="e.g. Aisha Rahman" required
                                        value={formData.reviewer_name} onChange={(e) => setFormData({ ...formData, reviewer_name: e.target.value })}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label htmlFor="role" className="form-label font-weight-bold small text-muted">Role / Title</label>
                                    <input 
                                        id="role"
                                        type="text" className="form-control" placeholder="e.g. International Student"
                                        value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label htmlFor="university" className="form-label font-weight-bold small text-muted">University / Location</label>
                                    <input 
                                        id="university"
                                        type="text" className="form-control" placeholder="e.g. UNSW Sydney"
                                        value={formData.university} onChange={(e) => setFormData({ ...formData, university: e.target.value })}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label htmlFor="rating" className="form-label font-weight-bold small text-muted">Rating (1 to 5 Stars)</label>
                                    <select 
                                        id="rating"
                                        className="form-control"
                                        value={formData.rating} onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) })}
                                    >
                                        <option value={5}>5 Stars ⭐⭐⭐⭐⭐</option>
                                        <option value={4}>4 Stars ⭐⭐⭐⭐</option>
                                        <option value={3}>3 Stars ⭐⭐⭐</option>
                                        <option value={2}>2 Stars ⭐⭐</option>
                                        <option value={1}>1 Star ⭐</option>
                                    </select>
                                </div>
                                
                                {/* ── DIRECT FILE UPLOAD INPUT ───────────────── */}
                                <div className="col-12 mb-3">
                                    <label htmlFor="avatar_file" className="form-label font-weight-bold small text-muted">
                                        Reviewer Avatar Photo (Select Image File)
                                    </label>
                                    <div className="d-flex align-items-center gap-3">
                                        <input 
                                            id="avatar_file"
                                            type="file" 
                                            accept="image/*"
                                            className="form-control-file border rounded p-2 text-muted small w-100"
                                            onChange={handleAvatarFileSelect}
                                            disabled={uploadingImage}
                                        />
                                        {uploadingImage && (
                                            <span className="spinner-border spinner-border-sm text-primary ml-2" role="status" />
                                        )}
                                    </div>

                                    {formData.avatar && (
                                        <div className="mt-2.5 d-flex align-items-center gap-2 bg-light p-2 rounded border" style={{ marginTop: '8px' }}>
                                            <img 
                                                src={getFullImageUrl(formData.avatar)} 
                                                alt="Avatar Preview" 
                                                style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                                            />
                                            <span className="small text-success font-weight-bold ml-2">
                                                ✓ Avatar image uploaded successfully!
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <div className="col-12 mb-3">
                                    <label htmlFor="review_text" className="form-label font-weight-bold small text-muted">Review Text *</label>
                                    <textarea 
                                        id="review_text"
                                        className="form-control" rows="4" placeholder="Enter full testimonial quote..." required
                                        value={formData.review_text} onChange={(e) => setFormData({ ...formData, review_text: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="d-flex justify-content-end gap-2 pt-2 border-top">
                                <button type="button" className="btn btn-secondary px-4 font-weight-bold mr-2" onClick={() => setShowAddModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary px-4 font-weight-bold" disabled={submitting || uploadingImage}>
                                    {submitting ? 'Saving...' : 'Add Testimonial'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default TestimonialsList;
